import React, { useEffect, useState } from 'react';
import { Container, Card, CardBody, Form, FormGroup, Label, Input, Button, FormFeedback, CardTitle, Row, Col, Alert } from "reactstrap";
import Breadcrumb from "Components/UI/Breadcrumb";
import * as yup from "yup";
import { useFormik } from "formik";
import { getBookingById, getBookingDocuments, updateDeliveryFrom } from 'helpers/API Calls/Operations/booking';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from 'Components/UI/Spinner';
import SignaturePadComponent from 'Components/Form/Signature';

interface CustomerInfo {
  name: string;
  address_line_1: string;
  address_line_2: string;
  town: string;
  complete_postcode: string;
  phone_no_home: string;
  phone_no_mobile: string;
  email: string;
  cnic: string;
}

interface Disclaimer {
  print_name: string;
  date: string;
  signature: string;
  customer: CustomerInfo;
}

interface AdditionalItemsChecklist {
  anchor_stacks: boolean;
  petrol_blower: boolean;
  electric_blower: boolean;
  sand_bags: boolean;
  groundsheet: boolean;
  extension_cable: boolean;
  other_equipment: string;
}

interface SafetyChecklist {
  area_clear: boolean;
  inflatable_stable: boolean;
  inflatable_anchor: boolean;
  blower_safe: boolean;
  cord_intact: boolean;
  inflatable_intact: boolean;
  equipment_inflated: boolean;
  agreement_read: boolean;
}

interface Questions {
  predicted_wind: string;
  actual_wind: string;
  inflatable_pressure: string;
  customer_advised: string;
}

interface DeliveryContent {
  disclaimer: Disclaimer;
  additional_items_checklist: AdditionalItemsChecklist;
  safety_checklist: SafetyChecklist;
  questions: Questions;
}

interface DeliveryDocument {
  id: number;
  booking_id: number;
  type: string;
  status: number;
  content: DeliveryContent;
  created_at: string;
  updated_at: string;
}

function Delivery() {
  const { id } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const [isSignaturePadVisible, setIsSignaturePadVisible] = useState<boolean>(true);
  
  const formik = useFormik<DeliveryContent>({
    initialValues: {
      disclaimer: {
        print_name: '',
        date: '',
        signature: '',
        customer: {
          name: '',
          address_line_1: '',
          address_line_2: '',
          town: '',
          complete_postcode: '',
          phone_no_home: '',
          phone_no_mobile: '',
          email: '',
          cnic: '',
        }
      },
      additional_items_checklist: {
        anchor_stacks: false,
        petrol_blower: false,
        electric_blower: false,
        sand_bags: false,
        groundsheet: false,
        extension_cable: false,
        other_equipment: '',
      },
      safety_checklist: {
        area_clear: false,
        inflatable_stable: false,
        inflatable_anchor: false,
        blower_safe: false,
        cord_intact: false,
        inflatable_intact: false,
        equipment_inflated: false,
        agreement_read: false,
      },
      questions: {
        predicted_wind: '',
        actual_wind: '',
        inflatable_pressure: '',
        customer_advised: '',
      }
    },
    validationSchema: yup.object().shape({
      disclaimer: yup.object({
        print_name: yup.string().required('Print name is required'),
        date: yup.string().required('Date is required'),
        signature: yup.string().required('Signature is required'),
        customer: yup.object({
          name: yup.string().required('Name is required'),
          address_line_1: yup.string().required('Address is required'),
          town: yup.string().required('Town is required'),
          complete_postcode: yup.string().required('Postcode is required'),
          phone_no_mobile: yup.string().required('Mobile number is required'),
          email: yup.string().email('Invalid email').required('Email is required'),
          cnic: yup.string().required('CNIC is required'),
          phone_no_home: yup.string().optional(),
        })
      }),
      additional_items_checklist: yup.object({
        other_equipment: yup.string()
      }),
      questions: yup.object({
        predicted_wind: yup.string().required('Predicted wind is required'),
        actual_wind: yup.string().required('Actual wind is required'),
        inflatable_pressure: yup.string().required('Inflatable pressure is required'),
        customer_advised: yup.string().required('Customer advice is required'),
      })
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const response = await updateDeliveryFrom(Number(id), values);
      setSubmitting(false);
      if(response.success){
        navigate('/bookings');
      }
    },
  });  // Add scroll to error effect
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && formik.isSubmitting) {
     
      // Helper function to flatten nested error object into dot notation paths
      const flattenErrors = (obj: any, prefix = ''): string[] => {
        return Object.keys(obj).reduce((paths: string[], key: string) => {
          const value = obj[key];
          const newPrefix = prefix ? `${prefix}.${key}` : key;
          
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            return [...paths, ...flattenErrors(value, newPrefix)];
          }
          return [...paths, newPrefix];
        }, []);
      };

      const errorPaths = flattenErrors(formik.errors);
      const firstErrorPath = errorPaths[0];
      
      if (firstErrorPath) {
        const element = document.getElementById(firstErrorPath);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
    }
  }, [formik.errors, formik.isSubmitting]);
  
  useEffect(() => {
    const getForm = async () => {
      setLoading(true);
      try {
        const bookingData = await getBookingById(Number(id));
        let customer_info; 
        if (bookingData.success) {
          customer_info = bookingData.data.customer;
        }
        const response = await getBookingDocuments('type=delivery&limit=1&booking_id=' + id);
        if (response.success && response?.data?.items?.length > 0) {
          const document: DeliveryDocument = response.data?.items?.[0];
          formik.setValues({
            disclaimer: {
              print_name: document.content.disclaimer.print_name || '',
              date: document.content.disclaimer.date || '',
              signature: document.content.disclaimer.signature || '',
              customer: {
                name: customer_info.name || document.content.disclaimer.customer.name || '',
                address_line_1: customer_info.address_line_1 || document.content.disclaimer.customer.address_line_1 || '',
                address_line_2: customer_info.address_line_2 || document.content.disclaimer.customer.address_line_2 || '',
                town: customer_info.name || document.content.disclaimer.customer.town || '',
                complete_postcode: customer_info.town || document.content.disclaimer.customer.complete_postcode || '',
                phone_no_home: customer_info.phone_no_home || document.content.disclaimer.customer.phone_no_home || '',
                phone_no_mobile: customer_info.phone_no_mobile || document.content.disclaimer.customer.phone_no_mobile || '',
                email: customer_info.email || document.content.disclaimer.customer.email || '',
                cnic: document.content.disclaimer.customer.cnic || ''
              }
            },
            additional_items_checklist: document.content.additional_items_checklist,
            safety_checklist: document.content.safety_checklist,
            questions: document.content.questions
          });
          if(document.content.disclaimer.signature) {
            setIsSignaturePadVisible(false);
          }
        }else {
          formik.setFieldValue('disclaimer.customer.name', customer_info.name || '');
          formik.setFieldValue('disclaimer.customer.address_line_1', customer_info.address_line_1 || '');
          formik.setFieldValue('disclaimer.customer.address_line_2', customer_info.address_line_2 || '');
          formik.setFieldValue('disclaimer.customer.town', customer_info.town || '');
          formik.setFieldValue('disclaimer.customer.complete_postcode', customer_info.complete_postcode || '');
          formik.setFieldValue('disclaimer.customer.phone_no_home', customer_info.phone_no_home || '');
          formik.setFieldValue('disclaimer.customer.phone_no_mobile', customer_info.phone_no_mobile || '');
          formik.setFieldValue('disclaimer.customer.email', customer_info.email || '');
        }
      } catch (error) {
        console.error('Error fetching delivery document:', error);
      }
      setLoading(false);
    };

    getForm();
  }, [id]);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Bookings", link: "/bookings" }, {label: 'Delivery Document'}]} />
        {loading ? <div className="position-relative vh-100"><Spinner/></div>
        :
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              {/* Disclaimer Section */}
              <div className="mb-4">
                {/* Customer Details Section */}
                <CardTitle tag="h5" className='mb-3'>Customer Details</CardTitle>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="disclaimer.customer.name">Customer Name <span className="text-danger">*</span></Label>
                      <Input
                        id="disclaimer.customer.name"
                        name="disclaimer.customer.name"
                        value={formik.values.disclaimer.customer.name}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.disclaimer?.customer?.name && formik.errors.disclaimer?.customer?.name)}
                        disabled
                      />
                      {formik.touched.disclaimer?.customer?.name && formik.errors.disclaimer?.customer?.name && (
                        <FormFeedback>{String(formik.errors.disclaimer.customer.name)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="disclaimer.customer.email">Email <span className="text-danger">*</span></Label>
                      <Input
                        type="email"
                        id="disclaimer.customer.email"
                        name="disclaimer.customer.email"
                        value={formik.values.disclaimer.customer.email}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.disclaimer?.customer?.email && formik.errors.disclaimer?.customer?.email)}
                        disabled
                      />
                      {formik.touched.disclaimer?.customer?.email && formik.errors.disclaimer?.customer?.email && (
                        <FormFeedback>{String(formik.errors.disclaimer.customer.email)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="disclaimer.customer.phone_no_mobile">Mobile Number <span className="text-danger">*</span></Label>
                      <Input
                        id="disclaimer.customer.phone_no_mobile"
                        name="disclaimer.customer.phone_no_mobile"
                        value={formik.values.disclaimer.customer.phone_no_mobile}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.disclaimer?.customer?.phone_no_mobile && formik.errors.disclaimer?.customer?.phone_no_mobile)}
                        disabled
                      />
                      {formik.touched.disclaimer?.customer?.phone_no_mobile && formik.errors.disclaimer?.customer?.phone_no_mobile && (
                        <FormFeedback>{String(formik.errors.disclaimer.customer.phone_no_mobile)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="disclaimer.customer.phone_no_home">Home Phone</Label>
                      <Input
                        id="disclaimer.customer.phone_no_home"
                        name="disclaimer.customer.phone_no_home"
                        value={formik.values.disclaimer.customer.phone_no_home}
                        onChange={formik.handleChange}
                        disabled
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="disclaimer.customer.address_line_1">Address Line 1 <span className="text-danger">*</span></Label>
                      <Input
                        id="disclaimer.customer.address_line_1"
                        name="disclaimer.customer.address_line_1"
                        value={formik.values.disclaimer.customer.address_line_1}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.disclaimer?.customer?.address_line_1 && formik.errors.disclaimer?.customer?.address_line_1)}
                        disabled
                      />
                      {formik.touched.disclaimer?.customer?.address_line_1 && formik.errors.disclaimer?.customer?.address_line_1 && (
                        <FormFeedback>{String(formik.errors.disclaimer.customer.address_line_1)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="disclaimer.customer.address_line_2">Address Line 2</Label>
                      <Input
                        id="disclaimer.customer.address_line_2"
                        name="disclaimer.customer.address_line_2"
                        value={formik.values.disclaimer.customer.address_line_2}
                        onChange={formik.handleChange}
                        disabled
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="disclaimer.customer.town">Town <span className="text-danger">*</span></Label>
                      <Input
                        id="disclaimer.customer.town"
                        name="disclaimer.customer.town"
                        value={formik.values.disclaimer.customer.town}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.disclaimer?.customer?.town && formik.errors.disclaimer?.customer?.town)}
                        disabled
                      />
                      {formik.touched.disclaimer?.customer?.town && formik.errors.disclaimer?.customer?.town && (
                        <FormFeedback>{String(formik.errors.disclaimer.customer.town)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="disclaimer.customer.complete_postcode">Postcode <span className="text-danger">*</span></Label>
                      <Input
                        id="disclaimer.customer.complete_postcode"
                        name="disclaimer.customer.complete_postcode"
                        value={formik.values.disclaimer.customer.complete_postcode}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.disclaimer?.customer?.complete_postcode && formik.errors.disclaimer?.customer?.complete_postcode)}
                        disabled
                      />
                      {formik.touched.disclaimer?.customer?.complete_postcode && formik.errors.disclaimer?.customer?.complete_postcode && (
                        <FormFeedback>{String(formik.errors.disclaimer.customer.complete_postcode)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="disclaimer.customer.cnic">CNIC <span className="text-danger">*</span></Label>
                      <Input
                        id="disclaimer.customer.cnic"
                        name="disclaimer.customer.cnic"
                        value={formik.values.disclaimer.customer.cnic}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.disclaimer?.customer?.cnic && formik.errors.disclaimer?.customer?.cnic)}
                      />
                      {formik.touched.disclaimer?.customer?.cnic && formik.errors.disclaimer?.customer?.cnic && (
                        <FormFeedback>{String(formik.errors.disclaimer.customer.cnic)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </div>

              {/* Additional Items Checklist */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Additional Items</CardTitle>
                <Row>
                  <Col md="6">
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="additional_items_checklist.anchor_stacks"
                        name="additional_items_checklist.anchor_stacks"
                        checked={formik.values.additional_items_checklist.anchor_stacks}
                        onChange={formik.handleChange}
                      />
                      <Label check>Anchor Stacks</Label>
                    </FormGroup>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="additional_items_checklist.petrol_blower"
                        name="additional_items_checklist.petrol_blower"
                        checked={formik.values.additional_items_checklist.petrol_blower}
                        onChange={formik.handleChange}
                      />
                      <Label check>Petrol Blower</Label>
                    </FormGroup>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="additional_items_checklist.electric_blower"
                        name="additional_items_checklist.electric_blower"
                        checked={formik.values.additional_items_checklist.electric_blower}
                        onChange={formik.handleChange}
                      />
                      <Label check>Electric Blower</Label>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="additional_items_checklist.sand_bags"
                        name="additional_items_checklist.sand_bags"
                        checked={formik.values.additional_items_checklist.sand_bags}
                        onChange={formik.handleChange}
                      />
                      <Label check>Sand Bags</Label>
                    </FormGroup>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="additional_items_checklist.groundsheet"
                        name="additional_items_checklist.groundsheet"
                        checked={formik.values.additional_items_checklist.groundsheet}
                        onChange={formik.handleChange}
                      />
                      <Label check>Groundsheet</Label>
                    </FormGroup>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="additional_items_checklist.extension_cable"
                        name="additional_items_checklist.extension_cable"
                        checked={formik.values.additional_items_checklist.extension_cable}
                        onChange={formik.handleChange}
                      />
                      <Label check>Extension Cable</Label>
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label htmlFor="additional_items_checklist.other_equipment">Other Equipment</Label>
                  <Input
                    type="textarea"
                    id="additional_items_checklist.other_equipment"
                    name="additional_items_checklist.other_equipment"
                    value={formik.values.additional_items_checklist.other_equipment}
                    onChange={formik.handleChange}
                    placeholder="Enter any other equipment that is not listed above"
                  />
                </FormGroup>
              </div>

              {/* Safety Checklist */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Safety Checklist</CardTitle>
                <Row>
                  <Col md="6">
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="safety_checklist.area_clear"
                        name="safety_checklist.area_clear"
                        checked={formik.values.safety_checklist.area_clear}
                        onChange={formik.handleChange}
                      />
                      <Label check>Setup area clear of debris and/or sharp objects</Label>
                    </FormGroup>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="safety_checklist.inflatable_stable"
                        name="safety_checklist.inflatable_stable"
                        checked={formik.values.safety_checklist.inflatable_stable}
                        onChange={formik.handleChange}
                      />
                      <Label check>Inflatable(s) are stable and on level ground</Label>
                    </FormGroup>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="safety_checklist.inflatable_anchor"
                        name="safety_checklist.inflatable_anchor"
                        checked={formik.values.safety_checklist.inflatable_anchor}
                        onChange={formik.handleChange}
                      />
                      <Label check>Inflatable(s) have been suitably anchored</Label>
                    </FormGroup>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="safety_checklist.blower_safe"
                        name="safety_checklist.blower_safe"
                        checked={formik.values.safety_checklist.blower_safe}
                        onChange={formik.handleChange}
                      />
                      <Label check>Blower(s) are securely attached & safe</Label>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="safety_checklist.cord_intact"
                        name="safety_checklist.cord_intact"
                        checked={formik.values.safety_checklist.cord_intact}
                        onChange={formik.handleChange}
                      />
                      <Label check>Electrical cords are in good working order</Label>
                    </FormGroup>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="safety_checklist.inflatable_intact"
                        name="safety_checklist.inflatable_intact"
                        checked={formik.values.safety_checklist.inflatable_intact}
                        onChange={formik.handleChange}
                      />
                      <Label check>No visible tears, rips or seams to the inflatable</Label>
                    </FormGroup>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="safety_checklist.equipment_inflated"
                        name="safety_checklist.equipment_inflated"
                        checked={formik.values.safety_checklist.equipment_inflated}
                        onChange={formik.handleChange}
                      />
                      <Label check>Equipment left fully inflated and working</Label>
                    </FormGroup>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="safety_checklist.agreement_read"
                        name="safety_checklist.agreement_read"
                        checked={formik.values.safety_checklist.agreement_read}
                        onChange={formik.handleChange}
                      />
                      <Label check>Hire agreement & safety instructions read</Label>
                    </FormGroup>
                  </Col>
                </Row>
              </div>

              {/* Questions */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Additional Questions</CardTitle>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="questions.predicted_wind">MET office predicted wind speed <span className="text-danger">*</span></Label>
                      <Input
                        type="text"
                        id="questions.predicted_wind"
                        name="questions.predicted_wind"
                        value={formik.values.questions.predicted_wind}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.questions?.predicted_wind && formik.errors.questions?.predicted_wind)}
                      />
                      {formik.touched.questions?.predicted_wind && formik.errors.questions?.predicted_wind && (
                        <FormFeedback>{String(formik.errors.questions.predicted_wind)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="questions.actual_wind">Actual wind speed <span className="text-danger">*</span></Label>
                      <Input
                        type="text"
                        id="questions.actual_wind"
                        name="questions.actual_wind"
                        value={formik.values.questions.actual_wind}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.questions?.actual_wind && formik.errors.questions?.actual_wind)}
                      />
                      {formik.touched.questions?.actual_wind && formik.errors.questions?.actual_wind && (
                        <FormFeedback>{String(formik.errors.questions.actual_wind)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="questions.inflatable_pressure">Inflatable pressure <span className="text-danger">*</span></Label>
                      <Input
                        type="text"
                        id="questions.inflatable_pressure"
                        name="questions.inflatable_pressure"
                        value={formik.values.questions.inflatable_pressure}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.questions?.inflatable_pressure && formik.errors.questions?.inflatable_pressure)}
                      />
                      {formik.touched.questions?.inflatable_pressure && formik.errors.questions?.inflatable_pressure && (
                        <FormFeedback>{String(formik.errors.questions.inflatable_pressure)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <Label htmlFor="questions.customer_advised">Customer Instructions <span className="text-danger">*</span></Label>
                      <Input
                        type="textarea"
                        id="questions.customer_advised"
                        name="questions.customer_advised"
                        value={formik.values.questions.customer_advised}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.questions?.customer_advised && formik.errors.questions?.customer_advised)}
                        placeholder="Enter any points or items that the customer has been advised of (max height/users, etc)"
                      />
                      {formik.touched.questions?.customer_advised && formik.errors.questions?.customer_advised && (
                        <FormFeedback>{String(formik.errors.questions.customer_advised)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <CardTitle tag="h4" className='mb-3'>Collector Information</CardTitle>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="disclaimer.print_name">Print Name <span className="text-danger">*</span></Label>
                      <Input
                        id="disclaimer.print_name"
                        name="disclaimer.print_name"
                        value={formik.values.disclaimer.print_name}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.disclaimer?.print_name && formik.errors.disclaimer?.print_name)}
                      />
                      {formik.touched.disclaimer?.print_name && formik.errors.disclaimer?.print_name && (
                        <FormFeedback>{String(formik.errors.disclaimer.print_name)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="disclaimer.date">Date <span className="text-danger">*</span></Label>
                      <Input
                        type="date"
                        id="disclaimer.date"
                        name="disclaimer.date"
                        value={formik.values.disclaimer.date.split(' ')[0]}
                        onChange={formik.handleChange}
                        invalid={!!(formik.touched.disclaimer?.date && formik.errors.disclaimer?.date)}
                      />
                      {formik.touched.disclaimer?.date && formik.errors.disclaimer?.date && (
                        <FormFeedback>{String(formik.errors.disclaimer.date)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <CardTitle tag="h4" className=''>Signature</CardTitle>
                {isSignaturePadVisible &&
                <>
                  <p className='text-muted mb-2'>Please handover to customer for signature.</p>
                  <Alert color="warning">
                    <strong>Note: </strong>
                    Please note that all persons using this equipment do so at their own risk. 
                    The person / organization hiring the equipment will be responsible/liable for any damage 
                    or injury occurring from or as a result of misuse or reckless use. 
                    These guidelines are for the safety of all people using this equipment, 
                    and it is the sole responsibility of the hirer to ensure they are adhered to. 
                    We cannot accept any responsibility for any injury caused to anyone using this equipment. 
                    I acknowledge receipt of the equipment in a good, clean condition and agree to 
                    abide by the terms and conditions listed above and return the equipment in the 
                    same condition it was received in.
                  </Alert>
                </>
                }
                <Row>
                  {(formik.values.disclaimer.signature && formik.values.disclaimer.signature !== '') ?
                  <img
                    src={formik.values.disclaimer.signature}
                    alt="Signature Placeholder"
                    style={{ height: 'auto', borderRadius: '8px' }}
                  />
                  :
                  <SignaturePadComponent
                    onSave={(base64) => {
                      formik.setFieldValue('disclaimer.signature', base64);
                    }}
                  />
                }
                 {formik.touched.disclaimer?.signature && formik.errors.disclaimer?.signature && (
                    <FormFeedback>{String(formik.errors.disclaimer?.signature)}</FormFeedback>
                  )}
                </Row>
              </div>

              <div className="d-flex mb-3 justify-content-end">
                <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>}
      </Container>
    </div>
  );
}

export default Delivery;
